import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Search, RefreshCw, Truck, Eye } from 'lucide-react';

const DispatchChallan = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [challans, setChallans] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [challanDetails, setChallanDetails] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingChallan, setViewingChallan] = useState(null);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'DESPATCH']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const challansRes = await api.get('/dispatch-challans');
      const ordersRes = await api.get('/sales-orders');
      setChallans(challansRes.data);
      setSalesOrders(ordersRes.data);
      setLoading(false);
      const [customersRes, partsRes] = await Promise.all([
        api.get('/customers'),
        api.get('/parts')
      ]);
      setCustomers(customersRes.data);
      setParts(partsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCustomerName = (customerId) => {
    const c = customers.find(x => x.id === customerId);
    return c ? `${c.code} - ${c.name}` : '-';
  };

  const getPartName = (partId) => {
    const p = parts.find(x => x.id === partId);
    return p ? `${p.part_no} - ${p.name}` : `ID: ${partId}`;
  };

  const getOrderDisplay = (orderId) => {
    const o = salesOrders.find(x => x.id === orderId);
    return o ? `SO-${o.order_no}` : `ID: ${orderId}`;
  };

  const getOrderCustomer = (orderId) => {
    const o = salesOrders.find(x => x.id === orderId);
    return o ? getCustomerName(o.customer_id) : '-';
  };

  const handleOpenDialog = () => {
    setFormData({
      challan_date: new Date().toISOString().split('T')[0],
      sales_order_id: '',
      vehicle_no: '',
      driver_name: '',
      driver_contact: '',
      remarks: '',
    });
    setChallanDetails([]);
    setPendingItems([]);
    setIsDialogOpen(true);
  };

  const handleOrderChange = async (orderId) => {
    setFormData(prev => ({ ...prev, sales_order_id: orderId }));
    setChallanDetails([]);
    if (orderId) {
      try {
        const res = await api.get(`/sales-orders/${orderId}/pending-items`);
        setPendingItems(res.data);
        // Auto-populate challan details with pending items
        setChallanDetails(res.data.map(item => ({
          part_id: item.part_id,
          part_name: item.part_name,
          part_no: item.part_no,
          pending_quantity: item.pending_quantity,
          quantity: item.pending_quantity, // default to full pending qty
        })));
      } catch {
        setPendingItems([]);
        toast.error('Failed to load pending items');
      }
    }
  };

  const handleDetailChange = (index, value) => {
    const updated = [...challanDetails];
    const qty = parseFloat(value) || 0;
    const maxQty = updated[index].pending_quantity;
    if (qty > maxQty) {
      toast.error(`Cannot exceed pending quantity of ${maxQty.toFixed(0)}`);
      return;
    }
    updated[index].quantity = qty;
    setChallanDetails(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sales_order_id || !formData.challan_date) {
      toast.error('Sales Order and Challan Date are required');
      return;
    }
    const validDetails = challanDetails.filter(d => d.quantity > 0);
    if (validDetails.length === 0) {
      toast.error('At least one item with quantity is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/dispatch-challans', {
        challan_date: formData.challan_date,
        sales_order_id: parseInt(formData.sales_order_id),
        vehicle_no: formData.vehicle_no || null,
        driver_name: formData.driver_name || null,
        driver_contact: formData.driver_contact || null,
        remarks: formData.remarks || null,
        details: validDetails.map(d => ({
          part_id: d.part_id,
          quantity: d.quantity,
        })),
      });
      toast.success('Dispatch Challan created');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Failed to create challan');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (challan) => {
    if (!window.confirm('Delete this dispatch challan? This will reverse dispatched quantities.')) return;
    try {
      await api.delete(`/dispatch-challans/${challan.id}`);
      toast.success('Challan deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const handleView = async (challan) => {
    try {
      const res = await api.get(`/dispatch-challans/${challan.id}`);
      setViewingChallan(res.data);
      setIsViewDialogOpen(true);
    } catch {
      toast.error('Failed to load challan details');
    }
  };

  // Filter only orders that have pending items (not fully dispatched/cancelled)
  const dispatchableOrders = salesOrders.filter(o =>
    ['PENDING', 'CONFIRMED'].includes(o.status)
  );

  const filteredChallans = challans.filter(ch => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (ch.challan_no ? ch.challan_no.toString() : '').includes(term) ||
      (ch.vehicle_no || '').toLowerCase().includes(term) ||
      getOrderDisplay(ch.sales_order_id).toLowerCase().includes(term) ||
      getOrderCustomer(ch.sales_order_id).toLowerCase().includes(term);
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="dispatch-challans">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Truck className="w-6 h-6 text-sky-500" />
            Dispatch Challans
          </h1>
          <p className="text-slate-500 text-sm">{challans.length} challans</p>
        </div>
        {canEdit && (
          <Button onClick={handleOpenDialog} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="new-challan-btn">
            <Plus className="w-4 h-4 mr-2" /> New Challan
          </Button>
        )}
      </div>

      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by Challan No, Vehicle, Order..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-sm" data-testid="search-input" />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : filteredChallans.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No dispatch challans found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Challan No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Sales Order</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Vehicle No</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredChallans.map((ch) => (
                    <TableRow key={ch.id}>
                      <TableCell className="font-mono font-bold">DC-{ch.challan_no}</TableCell>
                      <TableCell>{new Date(ch.challan_date).toLocaleDateString()}</TableCell>
                      <TableCell className="font-mono">{getOrderDisplay(ch.sales_order_id)}</TableCell>
                      <TableCell className="text-sm">{getOrderCustomer(ch.sales_order_id)}</TableCell>
                      <TableCell>{ch.vehicle_no || '-'}</TableCell>
                      <TableCell>{(ch.details ? ch.details.length : 0) || 0} items</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleView(ch)} className="h-8 w-8" data-testid={`view-challan-${ch.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(ch)} className="h-8 w-8 text-red-600" data-testid={`delete-challan-${ch.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Challan Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Dispatch Challan</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4">
              <div>
                <Label className="form-label">Challan Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={formData.challan_date} onChange={(e) => setFormData(prev => ({ ...prev, challan_date: e.target.value }))} required className="rounded-sm" data-testid="challan-date-input" />
              </div>
              <div>
                <Label className="form-label">Sales Order <span className="text-red-500">*</span></Label>
                <Select value={formData.sales_order_id} onValueChange={handleOrderChange}>
                  <SelectTrigger className="rounded-sm" data-testid="sales-order-select"><SelectValue placeholder="Select Sales Order" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {dispatchableOrders.map((o) => (
                      <SelectItem key={o.id} value={o.id.toString()}>
                        SO-{o.order_no} | {getCustomerName(o.customer_id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Vehicle No</Label>
                <Input value={formData.vehicle_no} onChange={(e) => setFormData(prev => ({ ...prev, vehicle_no: e.target.value }))} placeholder="MH-12-AB-1234" className="rounded-sm" data-testid="vehicle-no-input" />
              </div>
              <div>
                <Label className="form-label">Driver Name</Label>
                <Input value={formData.driver_name} onChange={(e) => setFormData(prev => ({ ...prev, driver_name: e.target.value }))} placeholder="Driver name" className="rounded-sm" data-testid="driver-name-input" />
              </div>
              <div>
                <Label className="form-label">Driver Contact</Label>
                <Input value={formData.driver_contact} onChange={(e) => setFormData(prev => ({ ...prev, driver_contact: e.target.value }))} placeholder="Phone" className="rounded-sm" data-testid="driver-contact-input" />
              </div>
              <div>
                <Label className="form-label">Remarks</Label>
                <Input value={formData.remarks} onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))} placeholder="Notes" className="rounded-sm" data-testid="remarks-input" />
              </div>
            </div>

            {/* Dispatch Items */}
            {challanDetails.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <h3 className="font-semibold text-slate-900 mb-3">Dispatch Items</h3>
                <div className="border rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Part</TableHead>
                        <TableHead className="text-right">Pending Qty</TableHead>
                        <TableHead className="text-right">Dispatch Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {challanDetails.map((d, index) => (
                        <TableRow key={index}>
                          <TableCell>{d.part_no} - {d.part_name}</TableCell>
                          <TableCell className="text-right font-mono text-orange-600">{d.pending_quantity.toFixed(0)}</TableCell>
                          <TableCell className="text-right">
                            <Input type="number" value={d.quantity} onChange={(e) => handleDetailChange(index, e.target.value)}
                              className="rounded-sm w-28 ml-auto" max={d.pending_quantity} data-testid={`dispatch-qty-${index}`} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {formData.sales_order_id && challanDetails.length === 0 && (
              <div className="mt-4 text-center py-6 text-slate-500 border border-dashed rounded-sm">
                No pending items for this sales order.
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={isSubmitting || challanDetails.length === 0} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="submit-challan-btn">
                {isSubmitting ? 'Creating...' : 'Create Challan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Challan Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Challan Details - DC-{(viewingChallan && viewingChallan.challan_no)}</DialogTitle>
          </DialogHeader>
          {viewingChallan && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><span className="text-sm text-slate-500">Challan Date</span><p className="font-medium">{new Date(viewingChallan.challan_date).toLocaleDateString()}</p></div>
                <div><span className="text-sm text-slate-500">Sales Order</span><p className="font-medium font-mono">{getOrderDisplay(viewingChallan.sales_order_id)}</p></div>
                <div><span className="text-sm text-slate-500">Customer</span><p className="font-medium">{getOrderCustomer(viewingChallan.sales_order_id)}</p></div>
                <div><span className="text-sm text-slate-500">Vehicle No</span><p className="font-medium">{viewingChallan.vehicle_no || '-'}</p></div>
                <div><span className="text-sm text-slate-500">Driver</span><p className="font-medium">{viewingChallan.driver_name || '-'}</p></div>
                <div><span className="text-sm text-slate-500">Driver Contact</span><p className="font-medium">{viewingChallan.driver_contact || '-'}</p></div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Dispatched Items ({(viewingChallan.details ? viewingChallan.details.length : 0) || 0})</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Part</TableHead>
                      <TableHead className="text-right">Dispatched Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(viewingChallan.details || []).map((d, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{getPartName(d.part_id)}</TableCell>
                        <TableCell className="text-right font-mono">{parseFloat(d.quantity).toFixed(0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {viewingChallan.remarks && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Remarks</h4>
                  <p className="text-slate-600">{viewingChallan.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DispatchChallan;
